import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import { Input, Select } from "@/components/base/Field";
import PhotoUploader from "@/components/base/PhotoUploader";
import { useToast } from "@/components/base/Toast";
import type { TemporaryAccount } from "@/components/feature/TemporaryPasswordDialog";
import { createUser, updateUser, type UserDto } from "@/services/userApi";
import CycleBadge from "./CycleBadge";
import type { AppUser, Role } from "@/types";

interface UserFormModalProps {
  open: boolean;
  onClose: () => void;
  user: AppUser | null;
  roles: Role[];
  onSaved: () => void;
  /** Compte créé : son mot de passe provisoire est à afficher une seule fois. */
  onCreated: (account: TemporaryAccount) => void;
}

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  avatar: string;
  roleId: string;
  isActive: boolean;
}

const emptyForm: FormState = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  avatar: "",
  roleId: "",
  isActive: true,
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Création ou modification d'un compte de l'école. À la création, le serveur génère un mot de passe
 * provisoire (affiché une fois) que l'utilisateur changera à sa première connexion.
 */
export default function UserFormModal({ open, onClose, user, roles, onSaved, onCreated }: UserFormModalProps) {
  const { notify } = useToast();
  const isEdit = user !== null;
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    if (user) {
      setForm({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phone: user.phone ?? "",
        avatar: user.avatar ?? "",
        roleId: user.roleId,
        isActive: user.isActive,
      });
    } else {
      setForm({ ...emptyForm, roleId: roles.find((r) => !r.isSystem)?.id ?? roles[0]?.id ?? "" });
    }
  }, [open, user, roles]);

  const selectedRole = roles.find((r) => r.id === form.roleId) ?? null;

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!form.firstName.trim()) next.firstName = "Le prénom est obligatoire.";
    if (!form.lastName.trim()) next.lastName = "Le nom est obligatoire.";
    if (!form.email.trim()) next.email = "L'email est obligatoire.";
    else if (!EMAIL_RE.test(form.email.trim())) next.email = "Format d'email invalide.";
    if (!form.roleId) next.roleId = "Sélectionnez un rôle.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      notify("Corrigez les champs en surbrillance.", "error");
      return;
    }
    setSaving(true);
    try {
      const dto: UserDto = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        avatar: form.avatar,
        roleId: form.roleId,
        isActive: form.isActive,
      };
      if (isEdit && user) {
        await updateUser(user.id, dto);
        notify(`Utilisateur ${form.firstName} ${form.lastName} mis à jour.`);
      } else {
        const { user: created, temporaryPassword, emailSent } = await createUser(dto);
        notify(`Utilisateur ${form.firstName} ${form.lastName} créé.`);
        onCreated({
          email: created.email,
          firstName: created.firstName,
          lastName: created.lastName,
          temporaryPassword,
          emailSent,
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Une erreur est survenue lors de l'enregistrement.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Modifier l'utilisateur" : "Ajouter un utilisateur"}
      subtitle={isEdit ? `${user?.firstName} ${user?.lastName}` : "Créez un accès pour un membre de l'école"}
      icon="ri-user-add-line"
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button icon="ri-save-3-line" onClick={handleSave} disabled={saving}>
            {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <PhotoUploader
          value={form.avatar}
          onChange={(avatar) => setForm({ ...form, avatar })}
          lastName={form.lastName}
          firstName={form.firstName}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Prénom"
            required
            value={form.firstName}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
            hint={errors.firstName}
          />
          <Input
            label="Nom"
            required
            value={form.lastName}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
            hint={errors.lastName}
          />
          <Input
            label="Email (identifiant de connexion)"
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            hint={errors.email}
          />
          <Input
            label="Téléphone"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </div>

        {!isEdit && (
          <p className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50/60 px-3 py-2.5 text-xs text-primary-800">
            <i className="ri-key-2-line text-sm"></i>
            Un mot de passe provisoire sera généré et affiché une seule fois après la création ;
            l'utilisateur le changera à sa première connexion.
          </p>
        )}

        <div className="rounded-lg border border-background-200 bg-background-100/60 p-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:items-end">
            <Select
              label="Rôle"
              required
              value={form.roleId}
              onChange={(e) => setForm({ ...form, roleId: e.target.value })}
              hint={errors.roleId}
            >
              <option value="">— Sélectionner un rôle —</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
            <div>
              <p className="mb-1.5 block text-xs font-medium text-foreground-700">Cycle hérité du rôle</p>
              <div className="flex h-[38px] items-center gap-2">
                {selectedRole ? (
                  <>
                    <CycleBadge scope={selectedRole.cycleScope} />
                    <span className="text-xs text-foreground-500">
                      {selectedRole.cycleScope === "GLOBAL" ? "Accès aux deux cycles" : "Accès limité à ce cycle"}
                    </span>
                  </>
                ) : (
                  <span className="text-xs text-foreground-400">Choisissez un rôle pour voir le cycle.</span>
                )}
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-4">
            <div>
              <p className="text-sm font-medium text-foreground-800">Statut du compte</p>
              <p className="text-xs text-foreground-500">
                {form.isActive ? "Le compte peut se connecter." : "Le compte est désactivé."}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={form.isActive}
              onClick={() => setForm({ ...form, isActive: !form.isActive })}
              className={`relative h-6 w-11 rounded-full border transition-colors cursor-pointer ${
                form.isActive ? "border-primary-500 bg-primary-600" : "border-background-300 bg-background-200"
              }`}
            >
              <span
                className={`absolute top-0.5 rounded-full bg-background-50 transition-transform ${
                  form.isActive ? "translate-x-[22px]" : "translate-x-0.5"
                }`}
                style={{ height: "18px", width: "18px" }}
              ></span>
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
